import type { Unsubscribe } from "../types/unsubscribe";
import { notifyEventListeners } from "../utils/notify-event-listeners";

export namespace RealtimeVoiceAdapter {
  export type Status =
    | {
        type: "starting" | "running";
      }
    | {
        type: "ended";
        reason: "finished" | "cancelled" | "error";
        error?: unknown;
      };

  export type Mode = "listening" | "speaking";

  export type TranscriptItem = {
    role: "user" | "assistant";
    text: string;
    isFinal?: boolean;
  };

  export type Session = {
    status: Status;
    isMuted: boolean;

    disconnect: () => void;
    mute: () => void;
    unmute: () => void;
    /**
     * Delivers typed text into the connected session. The runtime records the typed turn in the thread itself, so the session must not echo it through `onTranscript`. A session without it takes audio only.
     */
    sendText?: ((text: string) => void | Promise<void>) | undefined;

    onStatusChange: (callback: (status: Status) => void) => Unsubscribe;
    onTranscript: (
      callback: (transcript: TranscriptItem) => void,
    ) => Unsubscribe;
    onModeChange: (callback: (mode: Mode) => void) => Unsubscribe;
    onVolumeChange: (callback: (volume: number) => void) => Unsubscribe;
  };
}

export type RealtimeVoiceAdapter = {
  connect: (options: {
    abortSignal?: AbortSignal;
  }) => RealtimeVoiceAdapter.Session;
};

export type VoiceSessionControls = {
  disconnect: () => void;
  mute: () => void;
  unmute: () => void;
  /**
   * Delivers typed text to the provider. The session exposes `sendText` once these controls resolve and repeats its running status when they land after it, so status listeners can re-read the session; the runtime records the typed turn itself.
   */
  sendText?: ((text: string) => void | Promise<void>) | undefined;
};

export type VoiceSessionHelpers = {
  setStatus: (status: RealtimeVoiceAdapter.Status) => void;
  end: (reason: "finished" | "cancelled" | "error", error?: unknown) => void;
  emitTranscript: (item: RealtimeVoiceAdapter.TranscriptItem) => void;
  emitMode: (mode: RealtimeVoiceAdapter.Mode) => void;
  emitVolume: (volume: number) => void;
  isDisposed: () => boolean;
};

export function createVoiceSession(
  options: { abortSignal?: AbortSignal },
  setup: (helpers: VoiceSessionHelpers) => Promise<VoiceSessionControls>,
): RealtimeVoiceAdapter.Session {
  const statusCbs = new Set<(s: RealtimeVoiceAdapter.Status) => void>();
  const transcriptCbs = new Set<
    (t: RealtimeVoiceAdapter.TranscriptItem) => void
  >();
  const modeCbs = new Set<(m: RealtimeVoiceAdapter.Mode) => void>();
  const volumeCbs = new Set<(v: number) => void>();

  let currentStatus: RealtimeVoiceAdapter.Status = { type: "starting" };
  let isMuted = false;
  let disposed = false;
  let disconnected = false;
  let controls: VoiceSessionControls | null = null;
  let controlsDisconnected = false;
  const abortSignal = options.abortSignal;
  let abortHandler: (() => void) | undefined;

  const detachAbortHandler = () => {
    if (abortHandler) {
      abortSignal?.removeEventListener("abort", abortHandler);
      abortHandler = undefined;
    }
  };

  const cleanup = () => {
    disposed = true;
    statusCbs.clear();
    transcriptCbs.clear();
    modeCbs.clear();
    volumeCbs.clear();
  };

  const disconnectControls = () => {
    if (!controls || controlsDisconnected) return;
    controlsDisconnected = true;
    controls.disconnect();
  };

  const helpers: VoiceSessionHelpers = {
    setStatus: (status) => {
      if (disposed) return;
      currentStatus = status;
      notifyEventListeners(statusCbs, status, "Voice session");
    },
    end: (reason, error?) => {
      if (disposed) return;
      currentStatus = { type: "ended", reason, error };
      notifyEventListeners(statusCbs, currentStatus, "Voice session");
      cleanup();
    },
    emitTranscript: (item) => {
      if (disposed) return;
      notifyEventListeners(transcriptCbs, item, "Voice session");
    },
    emitMode: (mode) => {
      if (disposed) return;
      notifyEventListeners(modeCbs, mode, "Voice session");
    },
    emitVolume: (volume) => {
      if (disposed) return;
      notifyEventListeners(volumeCbs, volume, "Voice session");
    },
    isDisposed: () => disposed,
  };

  const session: RealtimeVoiceAdapter.Session = {
    get status() {
      return currentStatus;
    },
    get isMuted() {
      return isMuted;
    },
    get sendText() {
      if (disposed || !controls?.sendText) return undefined;
      return controls.sendText.bind(controls);
    },
    disconnect: () => {
      if (disconnected) return;
      disconnected = true;
      detachAbortHandler();
      if (currentStatus.type !== "ended") {
        currentStatus = { type: "ended", reason: "cancelled" };
        notifyEventListeners(statusCbs, currentStatus, "Voice session");
      }
      try {
        disconnectControls();
      } finally {
        cleanup();
      }
    },
    mute: () => {
      controls?.mute();
      isMuted = true;
    },
    unmute: () => {
      controls?.unmute();
      isMuted = false;
    },
    onStatusChange: (cb) => {
      statusCbs.add(cb);
      return () => statusCbs.delete(cb);
    },
    onTranscript: (cb) => {
      transcriptCbs.add(cb);
      return () => transcriptCbs.delete(cb);
    },
    onModeChange: (cb) => {
      modeCbs.add(cb);
      return () => modeCbs.delete(cb);
    },
    onVolumeChange: (cb) => {
      volumeCbs.add(cb);
      return () => volumeCbs.delete(cb);
    },
  };

  if (abortSignal) {
    abortHandler = () => session.disconnect();
    abortSignal.addEventListener("abort", abortHandler, { once: true });
    if (abortSignal.aborted) {
      session.disconnect();
      return session;
    }
  }

  const doSetup = async () => {
    try {
      if (disposed) return;
      controls = await setup(helpers);
      if (disposed) {
        disconnectControls();
        return;
      }
      if (isMuted) controls.mute();
      if (controls.sendText && currentStatus.type === "running")
        notifyEventListeners(statusCbs, currentStatus, "Voice session");
    } catch (error) {
      helpers.end("error", error);
    }
  };

  doSetup();
  return session;
}
